import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertCircle, CheckCircle2, DollarSign, TrendingUp, Wallet, Receipt, Download, Copy, MessageCircle, Plus } from "lucide-react";
import { toast } from "sonner";

const BRL = (n) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(n || 0));

const presetRanges = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const dow = now.getDay(); // 0 = domingo, 6 = sábado
  const iso = (dt) => dt.toISOString();
  // Semana calendário: Domingo 00:00 -> Sábado 23:59:59
  const sundayStart = new Date(y, m, d - dow, 0, 0, 0);
  const saturdayEnd = new Date(y, m, d - dow + 6, 23, 59, 59);
  // Quinzena calendário: começa no Domingo da semana anterior, termina no Sábado da semana atual
  const biweeklyStart = new Date(y, m, d - dow - 7, 0, 0, 0);
  return {
    week: {
      start: iso(sundayStart),
      end: iso(saturdayEnd),
    },
    biweekly: {
      start: iso(biweeklyStart),
      end: iso(saturdayEnd),
    },
    current_month: {
      start: iso(new Date(y, m, 1)),
      end: iso(new Date(y, m + 1, 0, 23, 59, 59)),
    },
    last_month: {
      start: iso(new Date(y, m - 1, 1)),
      end: iso(new Date(y, m, 0, 23, 59, 59)),
    },
  };
};

export default function EmployeeClosing() {
  const { apiCall } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [preset, setPreset] = useState("current_month");
  const [start, setStart] = useState(presetRanges().current_month.start);
  const [end, setEnd] = useState(presetRanges().current_month.end);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payType, setPayType] = useState("salario");
  const [payMethod, setPayMethod] = useState("pix");
  const [payAmount, setPayAmount] = useState("0");
  const [payDesc, setPayDesc] = useState("");
  const [detailModal, setDetailModal] = useState(null); // {type, row}
  const [receiptModal, setReceiptModal] = useState(null); // {payment_id, receipt}
  // Passo A: filtros de tabela + modais inline
  const [movementFilter, setMovementFilter] = useState("all"); // all|vale|falta|bonus|ajuste
  const [baseSalaryInfoOpen, setBaseSalaryInfoOpen] = useState(false);
  const [movModalOpen, setMovModalOpen] = useState(false);
  const [movForm, setMovForm] = useState({ type: "vale", amount: "0", method: "dinheiro", description: "" });
  // refs para rolagem
  const svcRef = useRef(null);
  const movRef = useRef(null);
  const payRef = useRef(null);

  // Load employees once
  useEffect(() => {
    (async () => {
      try {
        const res = await apiCall("get", "/employees");
        // iter63: mantém TODOS (incluindo inativos) para separação visual, mas prioriza ativos como default
        const all = res.data || [];
        setEmployees(all);
        const actives = all.filter((e) => e.active !== false);
        if (actives.length && !selectedEmployee) setSelectedEmployee(actives[0].employee_id);
      } catch {
        toast.error("Erro ao carregar funcionários");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle preset changes
  useEffect(() => {
    if (preset === "custom") return;
    const r = presetRanges()[preset];
    if (r) {
      setStart(r.start);
      setEnd(r.end);
    }
  }, [preset]);

  const loadClosing = async () => {
    if (!selectedEmployee) return;
    setLoading(true);
    try {
      const res = await apiCall(
        "get",
        `/finance/employee-closing?employee_id=${selectedEmployee}&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`,
      );
      setData(res.data);
      // pre-fill amount with saldo
      setPayAmount(String(res.data?.totals?.net_payable || 0));
    } catch (e) {
      toast.error("Erro ao carregar fechamento");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedEmployee && start && end) loadClosing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployee, start, end]);

  const totals = data?.totals || {};
  const kpis = useMemo(
    () => [
      { label: "Comissão bruta", value: totals.total_commission_gross, icon: TrendingUp, tone: "#CBA871" },
      { label: "Base salário", value: totals.base_salary, icon: DollarSign, tone: "#166534" },
      { label: "Vales", value: totals.vales, icon: Wallet, tone: "#dc2626" },
      { label: "Faltas", value: totals.faltas, icon: AlertCircle, tone: "#dc2626" },
      { label: "Bônus", value: totals.bonus, icon: DollarSign, tone: "#166534" },
      { label: "Já pago", value: (totals.salario_pago || 0) + (totals.comissao_paga || 0), icon: Receipt, tone: "#737373" },
      { label: "Saldo a pagar", value: totals.net_payable, icon: CheckCircle2, tone: "#166534", highlight: true },
    ],
    [totals],
  );

  const openDetail = (type, row) => setDetailModal({ type, row });

  const handleMarkVerified = async () => {
    try {
      await apiCall("post", "/finance/employee-closing/mark-verified", {
        employee_id: selectedEmployee,
        period_start: start,
        period_end: end,
      });
      toast.success("Fechamento marcado como conferido");
      loadClosing();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao marcar como conferido");
    }
  };

  // Passo A: rolagem suave até uma seção
  const scrollTo = (ref, filter) => {
    if (filter) setMovementFilter(filter);
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Click nos cards KPI
  const handleKpiClick = (label) => {
    if (label === "Comissão bruta") scrollTo(svcRef);
    else if (label === "Base salário") setBaseSalaryInfoOpen(true);
    else if (label === "Vales") scrollTo(movRef, "vale");
    else if (label === "Faltas") scrollTo(movRef, "falta");
    else if (label === "Bônus") scrollTo(movRef, "bonus");
    else if (label === "Já pago") scrollTo(payRef);
    else if (label === "Saldo a pagar") {
      if (data?.closing_status !== "pago") setPayModalOpen(true);
    }
  };

  // Lançar movimentação (vale/falta/bônus/ajuste) inline
  const handleCreateMovement = async () => {
    const amount = parseFloat(movForm.amount);
    if (!amount || amount <= 0) {
      toast.error("Informe um valor positivo");
      return;
    }
    try {
      await apiCall("post", "/employee-payments", {
        employee_id: selectedEmployee,
        type: movForm.type,
        amount,
        payment_method: movForm.method,
        description: movForm.description || null,
        date: new Date().toISOString(),
      });
      toast.success(`${movForm.type.charAt(0).toUpperCase() + movForm.type.slice(1)} de ${BRL(amount)} lançado`);
      setMovModalOpen(false);
      setMovForm({ type: "vale", amount: "0", method: "dinheiro", description: "" });
      loadClosing();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao lançar movimentação");
    }
  };

  const handleRegisterPayment = async () => {
    if (!payAmount || Number(payAmount) <= 0) {
      toast.error("Informe um valor positivo");
      return;
    }
    try {
      const payload = {
        employee_id: selectedEmployee,
        period_start: start,
        period_end: end,
        expected_commission_gross: Number(totals.total_commission_gross || 0),
        expected_base_salary: Number(totals.base_salary || 0),
        expected_vales: Number(totals.vales || 0),
        expected_faltas: Number(totals.faltas || 0),
        expected_bonus: Number(totals.bonus || 0),
        expected_ajustes: Number(totals.ajustes || 0),
        expected_net_payable: Number(totals.net_payable || 0),
        amount: Number(payAmount),
        payment_method: payMethod,
        payment_type: payType,
        description: payDesc || undefined,
        confirm: true,
      };
      const res = await apiCall("post", "/finance/employee-closing/register-payment", payload);
      toast.success("Pagamento registrado — fechamento marcado como PAGO");
      setPayModalOpen(false);
      setPayDesc("");
      // Abre o comprovante
      const payId = res?.data?.payment_id;
      if (payId) {
        try {
          const rcpt = await apiCall("get", `/finance/employee-closing/receipt/${payId}`);
          setReceiptModal({ payment_id: payId, receipt: rcpt.data });
        } catch {
          toast.error("Pagamento salvo, mas erro ao gerar comprovante");
        }
      }
      loadClosing();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.reason === "drift_detected") {
        toast.error("Os valores mudaram — a tela vai recarregar para você conferir de novo");
        loadClosing();
      } else {
        toast.error(det?.message || det || "Erro ao registrar pagamento");
      }
    }
  };

  const statusColor = {
    aberto: "#737373",
    conferido: "#CBA871",
    pago: "#166534",
  };

  return (
    <div className="space-y-6" data-testid="employee-closing-tab">
      {/* Filtros topo */}
      <Card className="p-4 border-border bg-card shadow-soft rounded-md">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-widest">Funcionário</Label>
            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger data-testid="closing-employee-select">
                <SelectValue placeholder="Selecione..." />
              </SelectTrigger>
              <SelectContent>
                {/* iter63: agrupa Ativos primeiro, Inativos abaixo com separador */}
                {employees.filter(e => e.active !== false).length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[10px] uppercase tracking-widest font-semibold text-emerald-700 bg-emerald-50/60">
                      Ativos
                    </div>
                    {employees.filter(e => e.active !== false).map((e) => (
                      <SelectItem key={e.employee_id} value={e.employee_id} data-testid={`closing-emp-active-${e.employee_id}`}>
                        {e.name}
                      </SelectItem>
                    ))}
                  </>
                )}
                {employees.filter(e => e.active === false).length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[10px] uppercase tracking-widest font-semibold text-slate-500 bg-slate-100 mt-1">
                      Inativos — cuidado ao pagar
                    </div>
                    {employees.filter(e => e.active === false).map((e) => (
                      <SelectItem key={e.employee_id} value={e.employee_id} data-testid={`closing-emp-inactive-${e.employee_id}`}>
                        {e.name} <span className="text-[9px] text-slate-500 ml-1">(inativo)</span>
                      </SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Período</Label>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger data-testid="closing-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Semana (Dom–Sáb)</SelectItem>
                <SelectItem value="biweekly">Quinzena (Dom–Sáb)</SelectItem>
                <SelectItem value="current_month">Mês atual</SelectItem>
                <SelectItem value="last_month">Mês anterior</SelectItem>
                <SelectItem value="custom">Personalizado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">De</Label>
            <Input
              type="datetime-local"
              value={start.slice(0, 16)}
              onChange={(e) => {
                setPreset("custom");
                setStart(new Date(e.target.value).toISOString());
              }}
              data-testid="closing-start-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Até</Label>
            <Input
              type="datetime-local"
              value={end.slice(0, 16)}
              onChange={(e) => {
                setPreset("custom");
                setEnd(new Date(e.target.value).toISOString());
              }}
              data-testid="closing-end-input"
            />
          </div>
        </div>
      </Card>

      {loading && (
        <div className="text-center py-8 text-muted-foreground text-sm">Carregando fechamento...</div>
      )}

      {data && !loading && (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {kpis.map((k) => (
              <Card
                key={k.label}
                onClick={() => handleKpiClick(k.label)}
                className={`p-4 border-border bg-card shadow-soft rounded-md cursor-pointer transition-all hover:shadow-md hover:-translate-y-0.5 ${k.highlight ? "ring-2 ring-primary" : ""}`}
                data-testid={`closing-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
                  <k.icon className="h-3 w-3" strokeWidth={1.5} style={{ color: k.tone }} />
                  {k.label}
                </div>
                <div className="mt-2 text-xl font-light" style={{ color: k.tone }}>
                  {BRL(k.value)}
                </div>
              </Card>
            ))}
          </div>

          {/* Status + ações */}
          <Card className="p-4 border-border bg-card shadow-soft rounded-md">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">Status do fechamento:</span>
                <span
                  className="text-xs uppercase tracking-widest font-medium"
                  style={{ color: statusColor[data.closing_status] || "#737373" }}
                  data-testid="closing-status-badge"
                >
                  {data.closing_status}
                </span>
                {data.closing_meta?.marked_verified_at && (
                  <span className="text-xs text-muted-foreground">
                    Conferido em {new Date(data.closing_meta.marked_verified_at).toLocaleString("pt-BR")}
                  </span>
                )}
                {data.closing_meta?.marked_paid_at && (
                  <span className="text-xs text-muted-foreground">
                    Pago em {new Date(data.closing_meta.marked_paid_at).toLocaleString("pt-BR")}
                  </span>
                )}
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => setMovModalOpen(true)}
                  data-testid="open-movement-btn"
                  title="Lançar vale, falta, bônus ou ajuste rapidamente"
                >
                  <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Lançar Vale/Falta/Bônus/Ajuste
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    apiCall("get", `/finance/employee-closing/export.csv?employee_id=${selectedEmployee}&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`, null, { responseType: "blob" })
                      .then((res) => {
                        const blob = new Blob([res.data], { type: "text/csv" });
                        const link = document.createElement("a");
                        link.href = URL.createObjectURL(blob);
                        const stamp = new Date().toISOString().slice(0, 10);
                        link.download = `fechamento_${data.employee.name.replace(/\s+/g, "_")}_${stamp}.csv`;
                        link.click();
                        URL.revokeObjectURL(link.href);
                      })
                      .catch(() => toast.error("Erro ao exportar CSV"));
                  }}
                  data-testid="export-csv-btn"
                >
                  <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Exportar CSV
                </Button>
                {data.closing_status === "aberto" && (
                  <Button variant="outline" onClick={handleMarkVerified} data-testid="mark-verified-btn">
                    Marcar como conferido
                  </Button>
                )}
                {data.closing_status !== "pago" && (
                  <Button
                    onClick={() => setPayModalOpen(true)}
                    data-testid="open-register-payment-btn"
                  >
                    Registrar Pagamento ({BRL(totals.net_payable)})
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Divergências */}
          {data.divergences?.length > 0 && (
            <Card
              className="p-4 border-yellow-500/40 bg-yellow-50 dark:bg-yellow-900/10 rounded-md"
              data-testid="closing-divergences-card"
            >
              <div className="flex items-center gap-2 text-sm font-medium text-yellow-800 dark:text-yellow-300">
                <AlertCircle className="h-4 w-4" strokeWidth={1.5} />
                {data.divergences.length} divergência(s) encontrada(s)
              </div>
              <div className="mt-2 space-y-1 max-h-32 overflow-y-auto text-xs">
                {data.divergences.slice(0, 10).map((d, i) => (
                  <div key={i} className="text-yellow-900 dark:text-yellow-200">
                    • <span className="font-mono text-[10px] mr-1">[{d.kind}]</span> {d.message}
                  </div>
                ))}
                {data.divergences.length > 10 && (
                  <div className="text-muted-foreground">
                    ... e mais {data.divergences.length - 10}
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Serviços realizados */}
          <Card ref={svcRef} className="border-border bg-card shadow-soft rounded-md overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="text-sm uppercase tracking-widest font-medium">
                Serviços realizados ({data.services_performed?.length || 0})
              </h3>
            </div>
            <div className="max-h-96 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                    <TableHead className="text-xs uppercase tracking-widest">Data</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Cliente</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Serviço</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Valor</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Taxa</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest text-right">Comissão</TableHead>
                    <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.services_performed?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm">
                        Nenhum serviço no período.
                      </TableCell>
                    </TableRow>
                  ) : (
                    data.services_performed?.map((s, i) => (
                      <TableRow
                        key={i}
                        className="cursor-pointer hover:bg-secondary/20"
                        onClick={() => openDetail("service", s)}
                        data-testid={`service-row-${i}`}
                      >
                        <TableCell className="text-xs">
                          {s.date ? new Date(s.date).toLocaleDateString("pt-BR") : "—"}
                        </TableCell>
                        <TableCell className="text-sm">{s.client_name}</TableCell>
                        <TableCell className="text-sm">{s.service_name}</TableCell>
                        <TableCell className="text-sm text-right">{BRL(s.service_price)}</TableCell>
                        <TableCell className="text-xs text-right">{s.commission_rate_applied}%</TableCell>
                        <TableCell className="text-sm text-right font-medium" style={{ color: "#CBA871" }}>
                          {BRL(s.commission_amount)}
                        </TableCell>
                        <TableCell>
                          <span
                            className="text-[10px] uppercase tracking-widest"
                            style={{
                              color:
                                s.row_status === "comanda_aberta"
                                  ? "#dc2626"
                                  : s.row_status === "fiado"
                                    ? "#CBA871"
                                    : "#166534",
                            }}
                          >
                            {s.row_status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          {/* Movimentações */}
          <Card ref={movRef} className="border-border bg-card shadow-soft rounded-md overflow-hidden">
            <div className="p-4 border-b border-border flex flex-col md:flex-row md:items-center md:justify-between gap-2">
              <h3 className="text-sm uppercase tracking-widest font-medium">
                Movimentações — vales, faltas, bônus, ajustes ({data.movements?.filter((m) => movementFilter === "all" || m.type === movementFilter).length || 0}{movementFilter !== "all" && ` de ${data.movements?.length || 0}`})
              </h3>
              <div className="flex gap-1 flex-wrap">
                {["all", "vale", "falta", "bonus", "ajuste"].map((f) => (
                  <button
                    key={f}
                    onClick={() => setMovementFilter(f)}
                    className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-md border ${movementFilter === f ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary/40"}`}
                    data-testid={`mov-filter-${f}`}
                  >
                    {f === "all" ? "todos" : f}
                  </button>
                ))}
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                  <TableHead className="text-xs uppercase tracking-widest">Data</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Tipo</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Descrição</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Forma</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Valor</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.movements?.filter((m) => movementFilter === "all" || m.type === movementFilter).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-6 text-muted-foreground text-sm">
                      Nenhuma movimentação {movementFilter !== "all" ? `do tipo "${movementFilter}"` : ""} no período.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.movements?.filter((m) => movementFilter === "all" || m.type === movementFilter).map((m, i) => (
                    <TableRow
                      key={m.payment_id}
                      className="cursor-pointer hover:bg-secondary/20"
                      onClick={() => openDetail("movement", m)}
                      data-testid={`movement-row-${i}`}
                    >
                      <TableCell className="text-xs">
                        {m.date ? new Date(m.date).toLocaleDateString("pt-BR") : "—"}
                      </TableCell>
                      <TableCell>
                        <span className="text-xs uppercase tracking-widest">{m.type}</span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {m.description || <em className="text-yellow-600">sem descrição</em>}
                      </TableCell>
                      <TableCell className="text-xs">{m.payment_method}</TableCell>
                      <TableCell className="text-sm text-right">{BRL(m.amount)}</TableCell>
                      <TableCell>
                        <span
                          className="text-[10px] uppercase tracking-widest"
                          style={{ color: m.row_status === "sem_descricao" ? "#dc2626" : "#166534" }}
                        >
                          {m.row_status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>

          {/* Pagamentos já feitos */}
          <Card ref={payRef} className="border-border bg-card shadow-soft rounded-md overflow-hidden">
            <div className="p-4 border-b border-border">
              <h3 className="text-sm uppercase tracking-widest font-medium">
                Pagamentos realizados no período ({data.payments_in_period?.length || 0})
              </h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                  <TableHead className="text-xs uppercase tracking-widest">Data</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Tipo</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Descrição</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest">Forma</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-right">Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.payments_in_period?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6 text-muted-foreground text-sm">
                      Nenhum pagamento realizado no período.
                    </TableCell>
                  </TableRow>
                ) : (
                  data.payments_in_period?.map((p, i) => (
                    <TableRow
                      key={p.payment_id}
                      className="cursor-pointer hover:bg-secondary/20"
                      onClick={() => openDetail("payment", p)}
                      data-testid={`payment-row-${i}`}
                    >
                      <TableCell className="text-xs">
                        {p.date ? new Date(p.date).toLocaleDateString("pt-BR") : "—"}
                      </TableCell>
                      <TableCell className="text-xs uppercase tracking-widest">{p.type}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{p.description}</TableCell>
                      <TableCell className="text-xs">{p.payment_method}</TableCell>
                      <TableCell className="text-sm text-right font-medium">{BRL(p.amount)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      {/* Modal Registrar Pagamento com breakdown */}
      <Dialog open={payModalOpen} onOpenChange={setPayModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="register-payment-modal">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">
              Conferir e registrar pagamento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="rounded-md border border-border p-3 bg-secondary/20 space-y-2">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                Resumo do fechamento — {data?.employee?.name}
              </div>
              <div className="grid grid-cols-2 gap-y-1 text-sm">
                <span className="text-muted-foreground">Comissão bruta</span>
                <span className="text-right font-medium">{BRL(totals.total_commission_gross)}</span>
                <span className="text-muted-foreground">(+) Base salário/período</span>
                <span className="text-right font-medium">{BRL(totals.base_salary)}</span>
                <span className="text-muted-foreground">(+) Bônus</span>
                <span className="text-right font-medium">{BRL(totals.bonus)}</span>
                <span className="text-muted-foreground">(+) Ajustes</span>
                <span className="text-right font-medium">{BRL(totals.ajustes)}</span>
                <span className="text-muted-foreground">(−) Vales</span>
                <span className="text-right font-medium">{BRL(totals.vales)}</span>
                <span className="text-muted-foreground">(−) Faltas</span>
                <span className="text-right font-medium">{BRL(totals.faltas)}</span>
                <span className="text-muted-foreground">(−) Já pago (salário)</span>
                <span className="text-right font-medium">{BRL(totals.salario_pago)}</span>
                <span className="text-muted-foreground">(−) Já pago (comissão)</span>
                <span className="text-right font-medium">{BRL(totals.comissao_paga)}</span>
                <div className="col-span-2 border-t border-border my-1" />
                <span className="font-medium">Saldo a pagar</span>
                <span className="text-right font-semibold text-lg" style={{ color: "#166534" }}>
                  {BRL(totals.net_payable)}
                </span>
              </div>
            </div>
            {data?.divergences?.length > 0 && (
              <div className="rounded-md border border-yellow-500/40 bg-yellow-50 dark:bg-yellow-900/10 p-3 text-xs text-yellow-900 dark:text-yellow-200">
                ⚠️ Existem {data.divergences.length} divergência(s) pendentes. Você ainda pode pagar, mas confira antes.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest">Classificação</Label>
                <Select value={payType} onValueChange={setPayType}>
                  <SelectTrigger data-testid="pay-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="salario">Fechamento consolidado</SelectItem>
                    <SelectItem value="comissao">Comissão já calculada</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">
                  Só classifica o pagamento. A comissão já vem dos serviços habilitados.
                </p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Forma</Label>
                <Select value={payMethod} onValueChange={setPayMethod}>
                  <SelectTrigger data-testid="pay-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                    <SelectItem value="transferencia">Transferência</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Valor</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  data-testid="pay-amount-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest">Descrição (opcional)</Label>
                <Input
                  value={payDesc}
                  onChange={(e) => setPayDesc(e.target.value)}
                  placeholder="Ex.: Fechamento de julho"
                  data-testid="pay-desc-input"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setPayModalOpen(false)} data-testid="pay-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={handleRegisterPayment} data-testid="pay-confirm-btn">
              Confirmar pagamento de {BRL(payAmount)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de detalhe (serviço/movimento/pagamento) */}
      <Dialog open={!!detailModal} onOpenChange={() => setDetailModal(null)}>
        <DialogContent className="sm:max-w-md" data-testid="closing-detail-modal">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-xl">
              Detalhe {detailModal?.type === "service" ? "do serviço" : detailModal?.type === "movement" ? "da movimentação" : "do pagamento"}
            </DialogTitle>
          </DialogHeader>
          {detailModal?.row && (
            <div className="text-xs space-y-2 mt-2">
              {Object.entries(detailModal.row).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-border pb-1">
                  <span className="text-muted-foreground uppercase tracking-widest">{k}</span>
                  <span className="text-right font-mono">
                    {typeof v === "number"
                      ? k.includes("price") || k.includes("amount") || k.includes("commission_amount")
                        ? BRL(v)
                        : String(v)
                      : v == null
                        ? "—"
                        : String(v)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Base salário — detalhe do cálculo */}
      <Dialog open={baseSalaryInfoOpen} onOpenChange={setBaseSalaryInfoOpen}>
        <DialogContent className="sm:max-w-md" data-testid="base-salary-info-modal">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-xl">
              Como o Base salário foi calculado
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm mt-2">
            <div className="rounded-md border border-border p-3 bg-secondary/20">
              <div className="text-2xl font-light" style={{ color: "#166534" }}>
                {BRL(totals.base_salary)}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Fonte: <span className="font-mono">{totals.base_salary_source || "—"}</span>
              </div>
            </div>
            <div className="text-xs space-y-1 text-muted-foreground">
              <div><strong>Configuração atual do funcionário:</strong></div>
              <div>Tipo: {data?.employee?.compensation?.type || "—"}</div>
              <div>Semanal: {BRL(data?.employee?.compensation?.weekly_amount)}</div>
              <div>Quinzenal: {BRL(data?.employee?.compensation?.biweekly_amount)}</div>
              <div>Mensal: {BRL(data?.employee?.compensation?.monthly_amount)}</div>
              <div>Diária: {BRL(data?.employee?.compensation?.daily_amount)}</div>
              <div>Auto no fechamento: {data?.employee?.compensation?.auto_include_in_closing ? "sim" : "não"}</div>
            </div>
            <div className="text-[10px] text-muted-foreground border-t border-border pt-2">
              Regras: 6–8 dias usa semanal, 13–16 usa quinzenal, 27–32 usa mensal, 1 dia usa diária.
              Se o valor não estiver cadastrado, deriva do valor mais próximo (ex.: quinzenal ausente → semanal × 2, mensal ausente → semanal × 4).
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Lançar Movimentação inline */}
      <Dialog open={movModalOpen} onOpenChange={setMovModalOpen}>
        <DialogContent className="sm:max-w-md" data-testid="movement-modal">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-xl">
              Lançar movimentação para {data?.employee?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <Label className="text-xs uppercase tracking-widest">Tipo</Label>
              <Select value={movForm.type} onValueChange={(v) => setMovForm({ ...movForm, type: v })}>
                <SelectTrigger data-testid="mov-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vale">Vale (desconta do saldo)</SelectItem>
                  <SelectItem value="falta">Falta (desconta do saldo)</SelectItem>
                  <SelectItem value="bonus">Bônus (soma no saldo)</SelectItem>
                  <SelectItem value="ajuste">Ajuste (soma no saldo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Forma</Label>
              <Select value={movForm.method} onValueChange={(v) => setMovForm({ ...movForm, method: v })}>
                <SelectTrigger data-testid="mov-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="dinheiro">Dinheiro</SelectItem>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="transferencia">Transferência</SelectItem>
                  <SelectItem value="registro">Só registro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Valor</Label>
              <Input type="number" step="0.01" value={movForm.amount}
                onChange={(e) => setMovForm({ ...movForm, amount: e.target.value })}
                data-testid="mov-amount-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-widest">Descrição/justificativa</Label>
              <Input value={movForm.description}
                onChange={(e) => setMovForm({ ...movForm, description: e.target.value })}
                placeholder="Ex: Falta 15/07 sem justificativa"
                data-testid="mov-desc-input" />
              <p className="text-[10px] text-muted-foreground mt-1">Sem descrição vira divergência no fechamento.</p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setMovModalOpen(false)} data-testid="mov-cancel-btn">
              Cancelar
            </Button>
            <Button onClick={handleCreateMovement} data-testid="mov-save-btn">
              Lançar {movForm.type}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de comprovante — aparece automaticamente após pagamento */}
      <Dialog open={!!receiptModal} onOpenChange={() => setReceiptModal(null)}>
        <DialogContent className="sm:max-w-xl" data-testid="receipt-modal">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">
              Comprovante gerado
            </DialogTitle>
          </DialogHeader>
          {receiptModal?.receipt && (
            <div className="space-y-4 mt-2">
              {/* Prévia do comprovante */}
              <div className="rounded-md border border-border bg-secondary/10 p-4">
                <div className="text-sm font-medium mb-2">{receiptModal.receipt.salon_name}</div>
                <div className="text-xs text-muted-foreground mb-3">
                  Funcionário <span className="text-foreground font-medium">{receiptModal.receipt.employee.name}</span>
                  {" · "}
                  Período: {receiptModal.receipt.period.label}
                </div>
                <div className="grid grid-cols-2 gap-y-1 text-xs">
                  <span className="text-muted-foreground">Comissão bruta</span>
                  <span className="text-right">{BRL(receiptModal.receipt.totals.commission_gross)}</span>
                  <span className="text-muted-foreground">(+) Bônus</span>
                  <span className="text-right">{BRL(receiptModal.receipt.totals.bonus)}</span>
                  <span className="text-muted-foreground">(+) Ajustes</span>
                  <span className="text-right">{BRL(receiptModal.receipt.totals.ajustes)}</span>
                  <span className="text-muted-foreground">(−) Vales</span>
                  <span className="text-right">{BRL(receiptModal.receipt.totals.vales)}</span>
                  <span className="text-muted-foreground">(−) Faltas</span>
                  <span className="text-right">{BRL(receiptModal.receipt.totals.faltas)}</span>
                  <span className="text-muted-foreground">(−) Já pago</span>
                  <span className="text-right">
                    {BRL((receiptModal.receipt.totals.salario_pago_prior || 0) + (receiptModal.receipt.totals.comissao_paga_prior || 0))}
                  </span>
                  <div className="col-span-2 border-t border-border my-1" />
                  <span className="font-medium">Pago agora</span>
                  <span className="text-right font-semibold text-lg" style={{ color: "#166534" }}>
                    {BRL(receiptModal.receipt.payment.amount)}
                  </span>
                </div>
                <div className="mt-3 text-xs text-muted-foreground">
                  {receiptModal.receipt.payment.method_label} · {receiptModal.receipt.payment.date}
                  {" · "}
                  Registrado por {receiptModal.receipt.registered_by.name}
                </div>
              </div>

              {/* Ações (nenhuma envia sozinha) */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    apiCall("get", `/finance/employee-closing/receipt/${receiptModal.payment_id}/download.pdf`, null, {
                      responseType: "blob",
                    }).then((res) => {
                      const blob = new Blob([res.data], { type: "application/pdf" });
                      const link = document.createElement("a");
                      link.href = URL.createObjectURL(blob);
                      link.download = `comprovante_${receiptModal.receipt.employee.name}_${receiptModal.payment_id}.pdf`;
                      link.click();
                      URL.revokeObjectURL(link.href);
                    }).catch(() => toast.error("Erro ao baixar PDF"));
                  }}
                  data-testid="receipt-download-pdf"
                >
                  <Download className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Baixar PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(receiptModal.receipt.text);
                      toast.success("Texto copiado — cole no WhatsApp ou onde precisar");
                    } catch {
                      toast.error("Erro ao copiar. Use CTRL+C na prévia acima.");
                    }
                  }}
                  data-testid="receipt-copy-text"
                >
                  <Copy className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Copiar texto
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!receiptModal.receipt.whatsapp_url}
                  onClick={() => {
                    if (receiptModal.receipt.whatsapp_url) {
                      window.open(receiptModal.receipt.whatsapp_url, "_blank", "noopener,noreferrer");
                    }
                  }}
                  title={
                    receiptModal.receipt.whatsapp_url
                      ? "Abre o WhatsApp com a mensagem pronta. Você ainda precisa apertar ENVIAR."
                      : "Funcionário sem telefone cadastrado"
                  }
                  data-testid="receipt-open-whatsapp"
                >
                  <MessageCircle className="h-4 w-4 mr-2" strokeWidth={1.5} />
                  Abrir WhatsApp
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setReceiptModal(null)}
                  data-testid="receipt-close"
                >
                  Fechar
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground text-center">
                Nenhuma mensagem é enviada automaticamente. Você decide se envia ou não.
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
